/**
 * Visualization Registry — Auto-discovers and exports all visualizations.
 *
 * To add a new visualization:
 * 1. Create your class implementing the Visualization interface.
 * 2. Import it here and add its constructor to the `visualizationFactories` array.
 * That's it — the visualization picker will find it automatically.
 */

import { Visualization } from '../types';
import { CustomVisualization } from '../custom/CustomVisualization';
import { loadCustomPresets } from '../custom/storage';

// Spectrum (Canvas 2D)
import { FrequencyBars } from './spectrum/FrequencyBars';
import { MirroredBars } from './spectrum/MirroredBars';
import { CircularSpectrum } from './spectrum/CircularSpectrum';
import { WaveformLine } from './spectrum/WaveformLine';

// Particles (Canvas 2D)
import { ParticleField } from './particles/ParticleField';
import { StarField } from './particles/StarField';

// Minimal / Ambient (Canvas 2D)
import { PulsingGradient } from './minimal/PulsingGradient';
import { GlowOrb } from './minimal/GlowOrb';

// Shader (WebGL)
import { PlasmaWave } from './shader/PlasmaWave';

/** Factory functions for creating fresh visualization instances */
const visualizationFactories: (() => Visualization)[] = [
  () => new FrequencyBars(),
  () => new MirroredBars(),
  () => new CircularSpectrum(),
  () => new WaveformLine(),
  () => new ParticleField(),
  () => new StarField(),
  () => new PulsingGradient(),
  () => new GlowOrb(),
  () => new PlasmaWave(),
];

/** All built-in visualizations — shared instances for UI listing/picking */
export const allVisualizations: Visualization[] = visualizationFactories.map(f => f());

/** Get all visualizations including custom ones (re-reads localStorage each call) */
export function getAllVisualizationsWithCustom(): Visualization[] {
  const customs = loadCustomPresets().map(p => new CustomVisualization(p));
  return [...allVisualizations, ...customs];
}

/** Get a visualization by ID — checks built-in and custom */
export function getVisualizationById(id: string): Visualization | undefined {
  return getAllVisualizationsWithCustom().find(v => v.id === id);
}

/** Create a fresh visualization instance by ID (for rendering — each canvas needs its own) */
export function createVisualizationById(id: string): Visualization | undefined {
  // Handle custom visualization IDs
  if (id.startsWith('custom-')) {
    const presetId = id.replace('custom-', '');
    const preset = loadCustomPresets().find(p => p.id === presetId);
    if (preset) return new CustomVisualization(preset);
    return undefined;
  }

  const idx = allVisualizations.findIndex(v => v.id === id);
  if (idx === -1) return undefined;
  return visualizationFactories[idx]();
}

/** Get all visualizations in a category */
export function getVisualizationsByCategory(category: string): Visualization[] {
  return getAllVisualizationsWithCustom().filter(v => v.category === category);
}
